import React, { useState, useEffect } from 'react';
import api from '../Service/api';
import './CostCalculator.css';

function CostCalculator() {
  const [companyName, setCompanyName] = useState('');
  const [rows, setRows] = useState([
    {
      id: 1,
      paperNo: '',
      gram: '',
      rate: '',
      size: '',
      length: '',
      total: 0
    }
  ]);
  const [grandTotal, setGrandTotal] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [filteredCustomers, setFilteredCustomers] = useState([]);

  // Generate a new session ID on component mount
  useEffect(() => {
    generateNewSessionId();
    fetchCustomers();
    fetchCompanies();
  }, []);

  // Generate new session ID
  const generateNewSessionId = () => {
    const newSessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    setSessionId(newSessionId);
    return newSessionId;
  };

  // Fetch customers from the endpoint using configured api
  const fetchCustomers = async () => {
    try {
      const response = await api.get('/product/getAllCustomers');
      console.log("=== Customer API Response ===");
      console.log("Full Response:", response);
      console.log("Response data:", response.data);
      
      let customerList = [];
      
      // Handle your specific response structure
      if (response.data) {
        // Your backend sends data in data.additionalDetail
        if (response.data.additionalDetail && Array.isArray(response.data.additionalDetail)) {
          console.log("Found customers in additionalDetail");
          customerList = response.data.additionalDetail;
        }
        // Fallback for other structures
        else if (Array.isArray(response.data)) {
          customerList = response.data;
        }
        else if (response.data.data && Array.isArray(response.data.data)) {
          customerList = response.data.data;
        }
        else if (response.data.customers && Array.isArray(response.data.customers)) {
          customerList = response.data.customers;
        }
      }
      
      console.log("Raw customer list:", customerList);
      
      // Map to consistent format - using customerName field
      const formattedCustomers = customerList.map(customer => ({
        id: customer.id,
        name: customer.customerName || customer.name || 'Unknown',
        email: customer.email || '',
        phone: customer.phoneNumber || customer.phone || '',
        address: customer.address || ''
      }));
      
      console.log("Formatted customers:", formattedCustomers);
      setCustomers(formattedCustomers);
      
      if (formattedCustomers.length === 0) {
        console.warn("No valid customers found in response");
      } else {
        console.log(`✅ Successfully loaded ${formattedCustomers.length} customers`);
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
      setCustomers([]);
    }
  };

  // Filter customers based on input
  const filterCustomers = (searchText) => {
    if (!searchText || searchText.trim() === '') {
      setFilteredCustomers([]);
      setShowCustomerDropdown(false);
      return;
    }
    
    const searchLower = searchText.toLowerCase().trim();
    const filtered = customers.filter(customer => {
      if (!customer || !customer.name) return false;
      return customer.name.toLowerCase().includes(searchLower);
    });
    
    console.log(`Searching for "${searchText}": Found ${filtered.length} customers`);
    setFilteredCustomers(filtered);
    setShowCustomerDropdown(filtered.length > 0);
  };

  // Handle company name change with customer filtering
  const handleCompanyNameChange = (e) => {
    const value = e.target.value;
    setCompanyName(value);
    filterCustomers(value);
  };

  // Select customer from dropdown
  const selectCustomer = (customerName) => {
    setCompanyName(customerName);
    setShowCustomerDropdown(false);
    setFilteredCustomers([]);
  };

  // Debug function
  const debugCustomers = () => {
    console.log("Current customers state:", customers);
    console.log("Customers count:", customers.length);
    if (customers.length > 0) {
      console.log("First customer:", customers[0]);
      alert(`✅ Found ${customers.length} customers!\nExample: ${customers[0]?.name || 'No name'}\nCheck console for full list.`);
    } else {
      alert(`❌ No customers found. Check:\n1. API endpoint\n2. Console for errors\n3. Response structure`);
    }
  };

  // Calculate total for a single row
  const calculateRowTotal = (gram, rate, size, length) => {
    const gramNum = parseFloat(gram) || 0;
    const rateNum = parseFloat(rate) || 0;
    const sizeNum = parseFloat(size) || 0;
    const lengthNum = parseFloat(length) || 0;
    
    const total = (gramNum * rateNum * sizeNum * lengthNum) / 1550000;
    return Math.round(total * 100) / 100;
  };

  // Update row data
  const updateRow = (id, field, value) => {
    const updatedRows = rows.map(row => {
      if (row.id === id) {
        const updatedRow = { ...row, [field]: value };
        
        updatedRow.total = calculateRowTotal(
          field === 'gram' ? value : updatedRow.gram,
          field === 'rate' ? value : updatedRow.rate,
          field === 'size' ? value : updatedRow.size,
          field === 'length' ? value : updatedRow.length
        );
        
        return updatedRow;
      }
      return row;
    });
    
    setRows(updatedRows);
  };

  // Add new row
  const addRow = () => {
    const newId = Math.max(...rows.map(r => r.id), 0) + 1;
    setRows([
      ...rows,
      {
        id: newId,
        paperNo: '',
        gram: '',
        rate: '',
        size: '',
        length: '',
        total: 0
      }
    ]);
  };

  // Delete row
  const deleteRow = (id) => {
    if (rows.length === 1) {
      alert("At least one row is required!");
      return;
    }
    setRows(rows.filter(row => row.id !== id));
  };

  // Calculate grand total
  useEffect(() => {
    const total = rows.reduce((sum, row) => sum + row.total, 0);
    setGrandTotal(Math.round(total * 100) / 100);
  }, [rows]);

  // Save calculation to backend
  const saveCalculation = async () => {
    if (!companyName.trim()) {
      alert("Please enter company name!");
      return;
    }

    if (rows.some(row => !row.paperNo || !row.gram || !row.rate || !row.size || !row.length)) {
      alert("Please fill all fields in all rows!");
      return;
    }

    setSaving(true);
    try {
      const calculationData = {
        companyName: companyName,
        sessionId: sessionId,
        rows: rows.map(row => ({
          paperNo: row.paperNo,
          gram: parseFloat(row.gram),
          rate: parseFloat(row.rate),
          size: parseFloat(row.size),
          length: parseFloat(row.length),
          total: row.total
        }))
      };

      const response = await api.post('/cost-calculator/save', calculationData);
      
      if (response.data.success) {
        alert("Calculation saved successfully!");
        generateNewSessionId();
        resetForm(false);
      } else {
        alert("Failed to save: " + response.data.message);
      }
    } catch (error) {
      console.error("Error saving calculation:", error);
      alert("Error saving calculation. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Reset form
  const resetForm = (resetCompany = true) => {
    setRows([
      {
        id: 1,
        paperNo: '',
        gram: '',
        rate: '',
        size: '',
        length: '',
        total: 0
      }
    ]);
    if (resetCompany) {
      setCompanyName('');
    }
  };

  // Load calculation by session ID
  const loadCalculation = async (sessionIdToLoad) => {
    setLoading(true);
    try {
      const response = await api.get(`/cost-calculator/get/${sessionIdToLoad}`);
      
      if (response.data) {
        setCompanyName(response.data.companyName);
        
        const loadedRows = response.data.rows.map((row, index) => ({
          id: index + 1,
          paperNo: row.paperNo || '',
          gram: row.gram || '',
          rate: row.rate || '',
          size: row.size || '',
          length: row.length || '',
          total: row.total || 0
        }));
        
        setRows(loadedRows);
        setSessionId(sessionIdToLoad);
        setShowHistory(false);
        alert("Calculation loaded successfully!");
      }
    } catch (error) {
      console.error("Error loading calculation:", error);
      alert("Error loading calculation. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch calculation history
  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await api.get('/api/cost-calculator/history');
      setHistory(response.data);
      setShowHistory(true);
    } catch (error) {
      console.error("Error fetching history:", error);
      alert("Error fetching history. Please try again.");
    } finally {
      setLoadingHistory(false);
    }
  };

  // Fetch companies list from saved calculations
  const fetchCompanies = async () => {
    try {
      const response = await api.get('/api/cost-calculator/companies');
      setCompanies(response.data);
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  };

  // Search by company name
  const searchByCompany = async () => {
    if (!selectedCompany) {
      alert("Please select a company!");
      return;
    }
    
    setLoadingHistory(true);
    try {
      const response = await api.get(`/api/cost-calculator/company/${selectedCompany}`);
      setHistory(response.data);
    } catch (error) {
      console.error("Error searching by company:", error);
      alert("Error searching. Please try again.");
    } finally {
      setLoadingHistory(false);
    }
  };

  // Search by date range
  const searchByDateRange = async () => {
    if (!dateRange.startDate || !dateRange.endDate) {
      alert("Please select both start and end dates!");
      return;
    }
    
    setLoadingHistory(true);
    try {
      const response = await api.get('/api/cost-calculator/date-range', {
        params: {
          startDate: new Date(dateRange.startDate).toISOString(),
          endDate: new Date(dateRange.endDate).toISOString()
        }
      });
      setHistory(response.data);
    } catch (error) {
      console.error("Error searching by date range:", error);
      alert("Error searching. Please try again.");
    } finally {
      setLoadingHistory(false);
    }
  };

  // Delete calculation session
  const deleteCalculation = async (sessionIdToDelete) => {
    if (window.confirm("Are you sure you want to delete this calculation?")) {
      try {
        await api.delete(`/api/cost-calculator/delete/${sessionIdToDelete}`);
        alert("Calculation deleted successfully!");
        if (showHistory) {
          fetchHistory();
        }
      } catch (error) {
        console.error("Error deleting calculation:", error);
        alert("Error deleting calculation. Please try again.");
      }
    }
  };

  // Start new calculation
  const startNewCalculation = () => {
    if (window.confirm("Start new calculation? Current unsaved data will be lost.")) {
      resetForm(true);
      generateNewSessionId();
      setSelectedSession(null);
    }
  };

  // Format number for display
  const formatNumber = (value) => {
    if (value === 0 || value === '') return '0.00';
    return typeof value === 'number' ? value.toFixed(2) : value;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="cost-calculator-container">
      <div className="calculator-header">
        <h1 className="title">💰 Cost Calculator</h1>
        <p className="subtitle">Calculate paper costs with precision</p>
      </div>

      {/* Company Name Input with Autocomplete */}
      <div className="company-section">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <label className="company-label" style={{ flex: 1 }}>
            🏢 Company Name
            <div className="autocomplete-wrapper">
              <input
                type="text"
                className="company-input"
                value={companyName}
                onChange={handleCompanyNameChange}
                onFocus={() => {
                  if (companyName && customers.length > 0) {
                    filterCustomers(companyName);
                  }
                }}
                placeholder="Enter or select company name..."
                autoComplete="off"
              />
              {showCustomerDropdown && filteredCustomers.length > 0 && (
                <div className="autocomplete-dropdown">
                  {filteredCustomers.map((customer, index) => (
                    <div
                      key={customer.id || index}
                      className="autocomplete-item"
                      onClick={() => selectCustomer(customer.name)}
                    >
                      <span className="customer-icon">🏢</span>
                      <span className="customer-name">{customer.name}</span>
                      {customer.phone && (
                        <span className="customer-phone">📞 {customer.phone}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </label>
        </div>
        {customers.length > 0 && (
          <div className="customer-hint">
            💡 Tip: Start typing to search from {customers.length} customers
          </div>
        )}
      </div>

      {/* Rest of your component remains the same */}
      <div className="table-container">
        <div className="table-wrapper">
          <table className="cost-table">
            <thead>
              <tr>
                <th>Paper #</th>
                <th>Gram (gsm)</th>
                <th>Rate</th>
                <th>Size (inches)</th>
                <th>Length (feet)</th>
                <th>Total</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td data-label="Paper #">
                    <input
                      type="text"
                      className="cell-input"
                      value={row.paperNo}
                      onChange={(e) => updateRow(row.id, 'paperNo', e.target.value)}
                      placeholder="e.g., P-001"
                    />
                  </td>
                  <td data-label="Gram (gsm)">
                    <input
                      type="number"
                      className="cell-input"
                      value={row.gram}
                      onChange={(e) => updateRow(row.id, 'gram', e.target.value)}
                      placeholder="Gram"
                      step="any"
                    />
                  </td>
                  <td data-label="Rate">
                    <input
                      type="number"
                      className="cell-input"
                      value={row.rate}
                      onChange={(e) => updateRow(row.id, 'rate', e.target.value)}
                      placeholder="Rate"
                      step="any"
                    />
                  </td>
                  <td data-label="Size (inches)">
                    <input
                      type="number"
                      className="cell-input"
                      value={row.size}
                      onChange={(e) => updateRow(row.id, 'size', e.target.value)}
                      placeholder="Size"
                      step="any"
                    />
                  </td>
                  <td data-label="Length (feet)">
                    <input
                      type="number"
                      className="cell-input"
                      value={row.length}
                      onChange={(e) => updateRow(row.id, 'length', e.target.value)}
                      placeholder="Length"
                      step="any"
                    />
                  </td>
                  <td data-label="Total" className="total-cell">
                    <span className="total-amount">{formatNumber(row.total)}</span>
                  </td>
                  <td data-label="Action" className="action-cell">
                    <button
                      className="delete-row-btn"
                      onClick={() => deleteRow(row.id)}
                      title="Delete row"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="add-row-section">
          <button className="add-row-btn" onClick={addRow}>
            + Add New Row
          </button>
        </div>

        <div className="grand-total-section">
          <div className="grand-total-card">
            <div className="grand-total-label">
              <span>💰 Grand Total</span>
              <small>(Sum of all rows)</small>
            </div>
            <div className="grand-total-amount">
               {formatNumber(grandTotal)}
            </div>
          </div>
        </div>

        <div className="action-buttons">
          <button className="action-btn save-btn" onClick={saveCalculation} disabled={saving}>
            {saving ? "Saving..." : "💾 Save Calculation"}
          </button>
          <button className="action-btn load-btn" onClick={fetchHistory}>
            📋 Load History
          </button>
          <button className="action-btn new-btn" onClick={startNewCalculation}>
            ✨ New Calculation
          </button>
        </div>

      </div>

      {/* History Modal and Loading Overlay remain the same */}
      {showHistory && (
        <div className="modal-overlay" onClick={() => setShowHistory(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📜 Calculation History</h2>
              <button className="close-modal" onClick={() => setShowHistory(false)}>×</button>
            </div>
            
            <div className="search-filters">
              <div className="filter-group">
                <select 
                  value={selectedCompany} 
                  onChange={(e) => setSelectedCompany(e.target.value)}
                  className="filter-select"
                >
                  <option value="">Select Company</option>
                  {companies.map(company => (
                    <option key={company} value={company}>{company}</option>
                  ))}
                </select>
                <button onClick={searchByCompany} className="filter-btn">Search by Company</button>
              </div>
              
              <div className="filter-group">
                <input
                  type="date"
                  className="filter-input"
                  value={dateRange.startDate}
                  onChange={(e) => setDateRange({...dateRange, startDate: e.target.value})}
                />
                <span>to</span>
                <input
                  type="date"
                  className="filter-input"
                  value={dateRange.endDate}
                  onChange={(e) => setDateRange({...dateRange, endDate: e.target.value})}
                />
                <button onClick={searchByDateRange} className="filter-btn">Search by Date</button>
              </div>
            </div>

            {loadingHistory ? (
              <div className="loading-spinner">Loading...</div>
            ) : (
              <div className="history-list">
                {history.length === 0 ? (
                  <p className="no-data">No calculations found</p>
                ) : (
                  history.map((item, index) => (
                    <div key={index} className="history-item">
                      <div className="history-info">
                        <div className="history-company">
                          <strong>🏢 {item.companyName}</strong>
                        </div>
                        <div className="history-details">
                          <span>📅 {formatDate(item.calculationDate)}</span>
                          <span>💰 Grand Total: {formatNumber(item.grandTotal)}</span>
                          <span>📊 Rows: {item.rows?.length || 0}</span>
                        </div>
                      </div>
                      <div className="history-actions">
                        <button 
                          className="history-btn load-history-btn"
                          onClick={() => loadCalculation(item.sessionId)}
                        >
                          Load
                        </button>
                        <button 
                          className="history-btn delete-history-btn"
                          onClick={() => deleteCalculation(item.sessionId)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner-large">Loading...</div>
        </div>
      )}
    </div>
  );
}

export default CostCalculator;